import { useState, useMemo } from 'react';

interface Institution {
  name: string;
  license: string;
  type: string;
  rwa: string;
  rank: string;
  color: string;
  founded: string;
  sandbox: string;
  bigEvents: string;
  rwaCase: string;
  note: string;
  url: string;
}

interface InstitutionCompareProps {
  institutions: Institution[];
  lang: string;
}

const COMPARE_FIELDS = [
  { key: 'license', labelZh: '牌照类型', labelEn: 'License Type', icon: 'ri-shield-star-line' },
  { key: 'type', labelZh: '机构类型', labelEn: 'Institution Type', icon: 'ri-building-2-line' },
  { key: 'rwa', labelZh: 'RWA业务范围', labelEn: 'RWA Services', icon: 'ri-briefcase-line' },
  { key: 'sandbox', labelZh: '沙盒参与', labelEn: 'Sandbox', icon: 'ri-test-tube-line' },
  { key: 'founded', labelZh: '成立年份', labelEn: 'Founded', icon: 'ri-calendar-line' },
  { key: 'bigEvents', labelZh: '重大事件', labelEn: 'Major Events', icon: 'ri-star-line' },
  { key: 'rwaCase', labelZh: 'RWA案例', labelEn: 'RWA Cases', icon: 'ri-file-list-3-line' },
];

export default function InstitutionCompare({ institutions, lang }: InstitutionCompareProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [showCompare, setShowCompare] = useState(false);

  const toggleSelect = (name: string) => {
    setSelected((prev) => {
      if (prev.includes(name)) return prev.filter((n) => n !== name);
      if (prev.length >= 4) return prev; // max 4
      return [...prev, name];
    });
  };

  const selectedInstitutions = useMemo(
    () => institutions.filter((i) => selected.includes(i.name)),
    [institutions, selected]
  );

  const t = (zh: string, en: string) => lang === 'zh' ? zh : en;

  return (
    <div className="mt-6">
      {/* Compare trigger bar */}
      <div className="flex items-center justify-between mb-4 p-3 rounded-xl border border-dashed border-gray-200 bg-gray-50">
        <div className="flex items-center gap-2">
          <i className="ri-scales-3-line text-[#7C3AED] text-base"></i>
          <span className="text-sm font-bold text-gray-700">{t('机构对比', 'Compare Institutions')}</span>
          <span className="text-xs text-gray-400">{t('勾选最多4家机构进行并排对比', 'Select up to 4 institutions to compare side by side')}</span>
        </div>
        <div className="flex items-center gap-2">
          {selected.length > 0 && (
            <>
              <span className="text-xs text-[#7C3AED] font-bold">
                {t(`已选 ${selected.length} 家`, `${selected.length} selected`)}
              </span>
              <button
                onClick={() => setShowCompare(true)}
                disabled={selected.length < 2}
                className="px-3 py-1.5 rounded-full text-xs font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: selected.length >= 2 ? '#7C3AED' : '#E5E7EB', color: selected.length >= 2 ? '#fff' : '#9CA3AF' }}
              >
                <i className="ri-bar-chart-horizontal-line mr-1"></i>
                {t('开始对比', 'Compare Now')}
              </button>
              <button
                onClick={() => setSelected([])}
                className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap"
              >
                {t('清空', 'Clear')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Institution selection chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        {institutions.map((inst) => {
          const isSelected = selected.includes(inst.name);
          return (
            <button
              key={inst.name}
              onClick={() => toggleSelect(inst.name)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap border"
              style={{
                background: isSelected ? `${inst.color}15` : '#F9FAFB',
                borderColor: isSelected ? inst.color : '#E5E7EB',
                color: isSelected ? inst.color : '#6B7280',
                transform: isSelected ? 'scale(1.04)' : 'scale(1)',
              }}
            >
              {isSelected && <i className="ri-checkbox-circle-fill text-xs"></i>}
              {!isSelected && <i className="ri-checkbox-blank-circle-line text-xs"></i>}
              {inst.name}
              <span className="text-[10px] opacity-60">{inst.rank}</span>
            </button>
          );
        })}
      </div>

      {/* Compare Modal */}
      {showCompare && selectedInstitutions.length >= 2 && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.55)' }}>
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <i className="ri-scales-3-line text-[#7C3AED] text-lg"></i>
                <span className="font-black text-gray-800 text-base">{t('机构对比', 'Institution Comparison')}</span>
                <span className="text-xs text-gray-400 font-normal">
                  {t(`对比 ${selectedInstitutions.length} 家机构`, `Comparing ${selectedInstitutions.length} institutions`)}
                </span>
              </div>
              <button
                onClick={() => setShowCompare(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            {/* Compare table */}
            <div className="overflow-auto flex-1 p-6">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr>
                    <th className="text-left py-3 px-4 text-gray-500 font-semibold text-xs w-32 bg-gray-50 rounded-tl-xl sticky left-0">
                      {t('对比项目', 'Compare Field')}
                    </th>
                    {selectedInstitutions.map((inst) => (
                      <th key={inst.name} className="py-3 px-4 text-center min-w-[180px]">
                        <div className="flex flex-col items-center gap-1">
                          <div
                            className="w-8 h-8 flex items-center justify-center rounded-xl text-white text-xs font-black"
                            style={{ background: inst.color }}
                          >
                            {inst.rank}
                          </div>
                          <span className="font-black text-gray-800 text-xs leading-tight">{inst.name}</span>
                          <a
                            href={inst.url}
                            target="_blank"
                            rel="nofollow noreferrer"
                            className="text-[10px] text-[#7C3AED] hover:underline flex items-center gap-0.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="ri-external-link-line"></i>
                            {t('官网', 'Website')}
                          </a>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {COMPARE_FIELDS.map((field, fi) => (
                    <tr key={field.key} className={fi % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}>
                      <td className="py-3 px-4 sticky left-0 bg-inherit">
                        <div className="flex items-center gap-1.5 text-xs font-bold text-gray-600 whitespace-nowrap">
                          <i className={`${field.icon} text-[#7C3AED] text-xs`}></i>
                          {lang === 'zh' ? field.labelZh : field.labelEn}
                        </div>
                      </td>
                      {selectedInstitutions.map((inst) => {
                        const val = (inst as Record<string, string>)[field.key] ?? '—';
                        const isSandbox = field.key === 'sandbox';
                        return (
                          <td key={inst.name} className="py-3 px-4 text-center align-top">
                            {isSandbox ? (
                              <span
                                className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-bold"
                                style={
                                  val === '是' || val.startsWith('是')
                                    ? { background: '#D1FAE5', color: '#065F46' }
                                    : { background: '#F3F4F6', color: '#6B7280' }
                                }
                              >
                                {val === '是' || val.startsWith('是') ? (
                                  <i className="ri-checkbox-circle-fill text-xs"></i>
                                ) : (
                                  <i className="ri-close-circle-line text-xs"></i>
                                )}
                                {val}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-700 leading-relaxed">{val}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Note row */}
                  <tr className="bg-[#F5F3FF]/50">
                    <td className="py-3 px-4 sticky left-0 bg-[#F5F3FF]/50">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-gray-600 whitespace-nowrap">
                        <i className="ri-information-line text-[#7C3AED] text-xs"></i>
                        {t('机构简介', 'About')}
                      </div>
                    </td>
                    {selectedInstitutions.map((inst) => (
                      <td key={inst.name} className="py-3 px-4 text-center align-top">
                        <p className="text-xs text-gray-600 leading-relaxed text-left">{inst.note}</p>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-xs text-gray-400">
                {t('数据来源：SFC官网，更新至2026年3月', 'Source: SFC official website, updated March 2026')}
              </span>
              <button
                onClick={() => setShowCompare(false)}
                className="px-4 py-1.5 rounded-full text-xs font-bold bg-[#7C3AED] text-white cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
              >
                {t('关闭对比', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
